import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MiniSite, MiniSiteGallery, MiniSiteImage } from '@worki/shared';
import { api } from '../lib/api.js';
import { Button, Field, Input, LoadingScreen, Textarea } from '../components/ui.js';
import { Icon } from '../components/Icon.js';
import { MiniSiteGalleryCard } from '../components/MiniSiteGalleryCard.js';
import { currentLocale } from '../i18n.js';
import { swipeStep } from '../lib/swipe.js';
import { AboutSection } from '../components/miniSite/AboutSection.js';
import { AlbumSideControls } from '../components/miniSite/AlbumSideControls.js';
import { ContactBar, whatsappHref as toWhatsapp } from '../components/miniSite/ContactBar.js';
import { RatingCircle } from '../components/miniSite/RatingCircle.js';
import { ReviewList } from '../components/miniSite/ReviewList.js';

/** How many albums show before "show all" — the same ten as the owner's screen. */
const ALBUMS_SHOWN = 10;

/*
 * Which albums this browser has hearted, so the heart is drawn filled on the
 * next visit. A convenience only: the server holds the count and decides a
 * second tap is an unlike, whatever this list says.
 */
const likedKey = (slug: string) => `worki.likes.${slug}`;
function readLiked(slug: string): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(likedKey(slug)) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

const EMPTY_FORM = { name: '', phone: '', email: '', message: '', galleryId: '', marketingConsent: false };

/**
 * The public storefront: the one screen the business's own customers see. Its
 * text comes from the dictionary like every other screen — it used to write its
 * Hebrew inline, which meant an English-speaking visitor got a Hebrew page and
 * a translator had no single place to work from.
 */
export function PublicMiniSitePage() {
  const { slug = '' } = useParams();
  const { t } = useTranslation();
  const locale = currentLocale();
  const site = useQuery({ queryKey: ['public-site', slug], queryFn: () => api.get<MiniSite>(`/public/sites/${slug}`) });
  const [active, setActive] = useState<{ gallery: MiniSiteGallery; index: number } | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showAll, setShowAll] = useState(false);
  const [liked, setLiked] = useState<string[]>(() => readLiked(slug));
  /** Counts the server answered with since the page loaded, by album. */
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const like = useMutation({
    mutationFn: (galleryId: string) =>
      api.post<{ liked: boolean; likeCount: number }>(`/public/sites/${slug}/galleries/${galleryId}/like`, {
        liked: !liked.includes(galleryId),
      }),
    onSuccess: (answer, galleryId) => {
      setLikeCounts((counts) => ({ ...counts, [galleryId]: answer.likeCount }));
      setLiked((current) => {
        const next = answer.liked ? [...new Set([...current, galleryId])] : current.filter((id) => id !== galleryId);
        try {
          localStorage.setItem(likedKey(slug), JSON.stringify(next));
        } catch {
          /* private window: the heart still works for this visit */
        }
        return next;
      });
    },
  });

  /**
   * `galleryId` is only meaningful when the visitor arrived through a gallery's
   * "get details" button. Sending the empty placeholder failed the uuid rule, so
   * the server rejected every inquiry that did not come from a gallery — which is
   * every inquiry typed straight into the contact form. Omit it when unset.
   */
  const inquiry = useMutation({
    mutationFn: () => {
      const { galleryId, ...rest } = form;
      return api.post(`/public/sites/${slug}/inquiries`, galleryId ? { ...rest, galleryId } : rest);
    },
    onSuccess: () => setForm(EMPTY_FORM),
  });

  const dir = locale === 'he' ? 'rtl' : 'ltr';

  /**
   * Arrows follow the reading direction: the key that points at the *next*
   * photo is ArrowLeft in Hebrew and ArrowRight in English. The rule used to be
   * written in a comment only — ArrowLeft advanced whatever the language was —
   * so an English visitor's gallery ran backwards under the arrow keys.
   */
  useEffect(() => {
    if (!active) return;
    const step = (delta: number) =>
      setActive((a) => (a ? { ...a, index: (a.index + delta + a.gallery.images.length) % a.gallery.images.length } : a));
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActive(null);
      if (event.key === 'ArrowLeft') step(dir === 'rtl' ? 1 : -1);
      if (event.key === 'ArrowRight') step(dir === 'rtl' ? -1 : 1);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [active, dir]);

  if (site.isLoading) return <LoadingScreen />;
  if (!site.data)
    return (
      <div className="center-screen" dir={dir}>
        {t('miniSite.public.notFound')}
      </div>
    );

  const s = site.data;
  const galleryCount = s.galleries.length;
  const reviewCount = s.reviews.length;
  const phoneHref = s.phone ? `tel:${s.phone.replace(/[^+\d]/g, '')}` : null;
  const whatsappHref = toWhatsapp(s.phone);
  const openGallery = (gallery: MiniSiteGallery, index = 0) => {
    setActive({ gallery, index });
    void api.post(`/public/sites/${slug}/galleries/${gallery.id}/view`);
  };
  const shareSite = () => {
    const url = window.location.href.split('#')[0]!;
    if (navigator.share) {
      void navigator.share({ title: s.name, url }).catch(() => undefined);
      return;
    }
    void navigator.clipboard?.writeText(url).catch(() => undefined);
  };
  const jumpToContact = (galleryId = '') => {
    setForm({ ...form, galleryId });
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="public-site" dir={dir}>
      {/*
        A stranger lands mid-page as often as at the top — from a shared gallery
        link, or after scrolling. The bar keeps the business's name, the way
        around the page and the phone within reach the whole way down, which the
        old in-hero pill row could not do once it scrolled away.
      */}
      <header className="ms-bar">
        <a className="ms-bar__brand" href="#top">
          {s.logoUrl && <img src={s.logoUrl} alt="" />}
          <span>{s.name}</span>
        </a>
        <nav className="ms-bar__nav">
          {galleryCount > 0 && <a href="#projects">{t('miniSite.projects')}</a>}
          {reviewCount > 0 && <a href="#reviews">{t('miniSite.reviews')}</a>}
          <a href="#contact">{t('miniSite.public.contact')}</a>
        </nav>
        {phoneHref ? (
          <a className="btn btn--primary btn--sm ms-bar__cta" href={phoneHref}>
            <Icon name="phone" size={16} /> {t('miniSite.public.callNow')}
          </a>
        ) : (
          <a className="btn btn--primary btn--sm ms-bar__cta" href="#contact">
            {t('miniSite.public.contact')}
          </a>
        )}
      </header>

      {/*
        Nitzan's gallery page, in the same order as the owner's screen and drawn
        from the same section components: cover, score, about, projects,
        reviews — then the inquiry form, which is where the leads come from,
        and the contact bar pinned to the bottom of the screen.
      */}
      <main className="flex-1 mx-auto w-full max-w-4xl px-4 pb-6 space-y-6" id="top">
        <section
          className="relative rounded-2xl overflow-hidden border border-border bg-muted mt-4"
          style={{ height: 'clamp(200px, 30vw, 340px)' }}
        >
          {s.coverUrl ? (
            <img src={s.coverUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-info/20" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/75 via-foreground/20 to-transparent" />
          <div className="absolute bottom-4 start-4 end-4 flex items-end gap-3">
            <div className="h-[92px] w-[92px] shrink-0 rounded-2xl overflow-hidden border-4 border-card bg-card">
              {s.logoUrl ? (
                <img src={s.logoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="h-full w-full flex items-center justify-center text-2xl font-bold text-primary">
                  {s.name.trim().charAt(0)}
                </span>
              )}
            </div>
            <div className="pb-2 min-w-0">
              <h1 className="text-xl md:text-2xl font-bold text-white drop-shadow truncate">{s.name}</h1>
              {s.slogan && <p className="text-sm text-white/85 drop-shadow truncate">{s.slogan}</p>}
            </div>
          </div>
        </section>

        <div className="flex justify-end -mt-2">
          <RatingCircle average={s.reviewAverage} ratedCount={s.ratedCount} breakdown={s.ratingBreakdown} />
        </div>

        <AboutSection site={s} />

        {/*
          The portfolio. Even empty it stays on the page with a friendly line,
          so the business looks like it has not uploaded yet rather than broken.
        */}
        <section id="projects" className="space-y-3" aria-labelledby="ms-projects-heading">
          <h2 id="ms-projects-heading" className="text-base font-bold">
            {t('miniSite.projects')} <span className="text-muted-foreground ltr-nums">({galleryCount})</span>
          </h2>
          {galleryCount === 0 ? (
            <p className="ms-empty">{t('miniSite.public.emptyGalleries')}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {(showAll ? s.galleries : s.galleries.slice(0, ALBUMS_SHOWN)).map((gallery) => (
                <MiniSiteGalleryCard
                  key={gallery.id}
                  gallery={{ ...gallery, likeCount: likeCounts[gallery.id] ?? gallery.likeCount }}
                  currency={s.currency}
                  locale={locale}
                  rtl={dir === 'rtl'}
                  onOpen={() => openGallery(gallery)}
                  liked={liked.includes(gallery.id)}
                  onLike={() => like.mutate(gallery.id)}
                  foot={
                    <Button variant="secondary" size="sm" onClick={() => jumpToContact(gallery.id)}>
                      {t('miniSite.public.getDetails')}
                    </Button>
                  }
                />
              ))}
            </div>
          )}
          {!showAll && galleryCount > ALBUMS_SHOWN && (
            <Button className="w-full" onClick={() => setShowAll(true)}>
              {t('miniSite.showAllAlbums', { count: galleryCount })}
            </Button>
          )}
          <hr className="border-border/60" />
        </section>

        <ReviewList reviews={s.reviews} />

        <section id="contact" className="ms-section ms-contact">
          <div className="ms-section__head">
            <h2>{t('miniSite.public.contactTitle')}</h2>
          </div>
          <div className="ms-contact__grid">
            {/*
              The form is the slow lane. Beside it sit the two ways to reach the
              business in one tap — the same calls the hero offers, repeated here
              because this is where a visitor who scrolled the whole page has
              decided to make contact, and asking them to scroll back up to do it
              by phone loses the ones who will not type.
            */}
            {(phoneHref || whatsappHref || s.email) && (
              <aside className="ms-facts">
                {phoneHref && (
                  <a href={phoneHref}>
                    <Icon name="phone" size={18} />
                    <span>
                      <strong>{t('miniSite.public.callNow')}</strong>
                      <span className="ltr-nums">{s.phone}</span>
                    </span>
                  </a>
                )}
                {whatsappHref && (
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer">
                    <Icon name="external" size={18} />
                    <span>
                      <strong>{t('miniSite.public.whatsapp')}</strong>
                    </span>
                  </a>
                )}
                {s.email && (
                  <a href={`mailto:${s.email}`}>
                    <Icon name="mail" size={18} />
                    <span>
                      <strong>{t('customer.email')}</strong>
                      <span>{s.email}</span>
                    </span>
                  </a>
                )}
              </aside>
            )}
            {inquiry.isSuccess ? (
              <p className="ms-sent" role="status">
                {t('miniSite.public.sent')}
              </p>
            ) : (
              <form
                className="ms-form"
                aria-describedby="ms-privacy"
                onSubmit={(event) => {
                  event.preventDefault();
                  inquiry.mutate();
                }}
              >
                {/*
                  Privacy Protection Law s.11 wants the visitor told what is being
                  collected and who ends up holding it *before* they type it, so the
                  notice sits above the first field rather than under the button, and
                  is tied to the form so a screen reader reads it on entry.
                */}
                <p className="muted" id="ms-privacy">
                  {t('miniSite.public.privacyNotice')}
                </p>
                <div className="form-grid">
                  <Field label={t('customer.name')} htmlFor="ms-name">
                    <Input
                      id="ms-name"
                      required
                      value={form.name}
                      onChange={(event) => setForm({ ...form, name: event.target.value })}
                    />
                  </Field>
                  <Field label={t('customer.phone')} htmlFor="ms-phone">
                    <Input
                      id="ms-phone"
                      required
                      dir="ltr"
                      value={form.phone}
                      onChange={(event) => setForm({ ...form, phone: event.target.value })}
                    />
                  </Field>
                  <Field label={t('customer.email')} htmlFor="ms-email">
                    <Input
                      id="ms-email"
                      type="email"
                      dir="ltr"
                      value={form.email}
                      onChange={(event) => setForm({ ...form, email: event.target.value })}
                    />
                  </Field>
                </div>
                <Field label={t('miniSite.public.message')} htmlFor="ms-message">
                  <Textarea
                    id="ms-message"
                    value={form.message}
                    onChange={(event) => setForm({ ...form, message: event.target.value })}
                  />
                </Field>
                {/*
                  Unticked by default, and deliberately outside the submit path: the
                  inquiry sends either way, which is what `consentNote` promises. A
                  pre-ticked box would not be the prior consent Communications Law
                  s.30A asks the business to prove.
                */}
                <label className="checkbox-label" htmlFor="ms-consent">
                  <input
                    id="ms-consent"
                    type="checkbox"
                    className="checkbox"
                    aria-describedby="ms-consent-note"
                    checked={form.marketingConsent}
                    onChange={(event) => setForm({ ...form, marketingConsent: event.target.checked })}
                  />
                  {t('miniSite.public.consentLabel')}
                </label>
                <p className="muted" id="ms-consent-note" style={{ fontSize: 'var(--text-sm)' }}>
                  {t('miniSite.public.consentNote')}
                </p>
                {/* A failed send used to leave the form silent, so a visitor could not tell their lead had been lost. */}
                {inquiry.isError && (
                  <p role="alert" className="field__error">
                    {t('miniSite.public.sendFailed')}
                  </p>
                )}
                <Button type="submit" variant="primary" size="lg" loading={inquiry.isPending}>
                  {t('miniSite.public.submit')}
                </Button>
              </form>
            )}
          </div>
        </section>
      </main>

      <div className="sticky bottom-0 z-20 bg-card/95 backdrop-blur border-t border-border">
        <ContactBar phone={s.phone} onShare={shareSite} />
      </div>

      <footer className="ms-footer">
        <p>{s.name}</p>
        {(s.phone || s.email) && (
          <p>
            {s.phone && (
              <a className="ltr-nums" href={phoneHref!}>
                {s.phone}
              </a>
            )}
            {s.phone && s.email && <span> · </span>}
            {s.email && <a href={`mailto:${s.email}`}>{s.email}</a>}
          </p>
        )}
        <p>
          <a href="https://worki-top.duckdns.org" target="_blank" rel="noopener noreferrer">
            {t('miniSite.public.builtWith')}
          </a>
        </p>
      </footer>

      {active && (
        <Lightbox
          images={active.gallery.images}
          index={active.index}
          onIndex={(index) => setActive({ ...active, index })}
          onClose={() => setActive(null)}
          rtl={dir === 'rtl'}
          title={active.gallery.name}
          description={active.gallery.description}
        />
      )}
    </div>
  );
}

function Lightbox({
  title,
  description,
  images,
  index,
  onIndex,
  onClose,
  rtl,
}: {
  title: string;
  description: string | null;
  images: MiniSiteImage[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  rtl: boolean;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  /**
   * UI-7.2 — the photo is paged with the finger, and enlarged by tapping twice.
   *
   * SITE-006 had taken the swipe out and left the paging to the two arrows: a
   * drag that starts on the photo is often someone scrolling on, and the swipe
   * as it was read every drag as paging. The arrows are what went wrong with
   * that — pinned over the photo, they are exactly where the thumb holding the
   * phone already is. So the swipe comes back with the guard the first one
   * lacked (`swipeStep` ignores anything that travels further down the screen
   * than across it) and the arrows come off.
   */
  const [zoomed, setZoomed] = useState(false);
  /* Where the finger went down, so where it comes up can be read. */
  const touch = useRef<{ x: number; y: number } | null>(null);

  /* Paging opens the next photo fitted. Carrying the magnifier across would
     leave the frame showing a corner of a picture nobody had zoomed into. */
  useEffect(() => setZoomed(false), [index]);

  /**
   * The same trap Modal.tsx runs (UI-006). The lightbox claimed `aria-modal`
   * while Tab walked straight out of it into the page underneath, so a keyboard
   * visitor lost the close button behind the overlay and, on the way out, never
   * got focus back to the thumbnail they had opened.
   *
   * Escape and the arrows stay on the owner's window listener: they change
   * `active`, which lives up there, and duplicating them here would give the
   * lightbox two ways to close.
   */
  useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;

    const focusable = () =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => !el.hasAttribute('disabled'));

    focusable()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreFocusRef.current?.focus();
    };
  }, []);

  if (!images[index]) return null;
  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      ref={dialogRef}
      onTouchStart={(event) => {
        const finger = event.touches[0];
        touch.current = finger ? { x: finger.clientX, y: finger.clientY } : null;
      }}
      onTouchEnd={(event) => {
        const from = touch.current;
        const finger = event.changedTouches[0];
        touch.current = null;
        if (!from || !finger || images.length < 2) return;
        const step = swipeStep(finger.clientX - from.x, finger.clientY - from.y, rtl);
        if (step) onIndex((index + step + images.length) % images.length);
      }}
    >
      <button className="lightbox__close" aria-label={t('common.close')} onClick={onClose}>
        <Icon name="close" />
      </button>
      {/* The frame keeps its size whatever is in it, so paging never makes the
          overlay jump; only the picture inside it grows. */}
      <div className={`lightbox__frame${zoomed ? ' lightbox__frame--zoomed' : ''}`}>
        <button
          type="button"
          className="lightbox__zoom"
          aria-label={t(zoomed ? 'common.zoomOut' : 'common.zoomIn')}
          aria-pressed={zoomed}
          onDoubleClick={() => setZoomed((was) => !was)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            setZoomed((was) => !was);
          }}
        >
          <img src={images[index]!.url} alt={images[index]!.altText ?? ''} />
        </button>
        {!zoomed && images.length > 1 && <AlbumSideControls rtl={rtl} onMove={(step) => onIndex((index + step + images.length) % images.length)} />}
      </div>
      <div className="lightbox__album-info"><h2>{title}</h2>{description && <p>{description}</p>}</div>
      {/* Where the visitor is in the strip — the arrows move silently
          otherwise, and a gallery of three reads as a gallery of one. */}
      {images.length > 1 && (
        <p className="lightbox__count ltr-nums" aria-hidden="true">
          {index + 1} / {images.length}
        </p>
      )}
    </div>
  );
}
